import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back" : "Welcome to SHIOKOI");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/account";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen pt-28 pb-20 px-6 flex flex-col items-center bg-sand">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Logo tone="dark" className="h-20 w-auto mx-auto" />
        </div>

        <div className="flex mb-8 border border-charcoal/15 rounded-full overflow-hidden">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 py-3 overline transition-colors ${mode === m ? "bg-ocean text-sand" : "bg-transparent text-charcoal/60"}`}
            >
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <button
          data-testid="google-login"
          onClick={google}
          className="w-full flex items-center justify-center gap-3 border border-charcoal/25 py-3.5 mb-6 hover:bg-linen/50 transition-colors text-sm font-medium"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6 text-charcoal/40 text-xs">
          <div className="h-px bg-charcoal/15 flex-1" /> or <div className="h-px bg-charcoal/15 flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && (
            <div>
              <label className="overline text-charcoal/60">Name</label>
              <input
                data-testid="name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-charcoal/30 py-2.5 focus:outline-none focus:border-charcoal"
              />
            </div>
          )}
          <div>
            <label className="overline text-charcoal/60">Email</label>
            <input
              data-testid="email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border-b border-charcoal/30 py-2.5 focus:outline-none focus:border-charcoal"
            />
          </div>
          <div>
            <label className="overline text-charcoal/60">Password</label>
            <input
              data-testid="password-input"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent border-b border-charcoal/30 py-2.5 focus:outline-none focus:border-charcoal"
            />
          </div>
          <button
            data-testid="submit-auth"
            disabled={busy}
            className="w-full bg-ocean text-sand py-4 overline hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
          {mode === "login" && (
            <div className="text-center">
              <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-charcoal/60 hover:text-charcoal transition-colors">
                Forgot your password?
              </Link>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
